import React from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import {
  dateRangeGroupSx,
  getDateAdapterLocale,
  getDatePickerLocaleText,
  groupedInnerEndFieldErrorSx,
  groupedInnerEndFieldSx,
  groupedInnerFieldErrorSx,
  groupedInnerFieldSx,
  popperSx,
} from './datePickerStyles';
import { BRAND } from '../../theme/brandTokens';
import { useStableDatePickerPopup } from './useStableDatePickerPopup';

const buildInnerFieldSlotProps = ({ label, error, edge = 'start', onBlur }) => {
  const fieldSxByEdge = {
    start: error ? groupedInnerFieldErrorSx : groupedInnerFieldSx,
    end: error ? groupedInnerEndFieldErrorSx : groupedInnerEndFieldSx,
  };

  return {
    textField: {
      label,
      fullWidth: true,
      sx: fieldSxByEdge[edge],
      onBlur,
    },
    popper: {
      sx: popperSx,
      disableScrollLock: true,
    },
  };
};

const SalovareDateRangePicker = ({
  label,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  onStartBlur,
  onEndBlur,
  fromLabel,
  toLabel,
  error = false,
  helperText,
  helperContent,
  format = 'dd/MM/yyyy',
  disablePast = false,
  disableFuture = false,
  endOptional = false,
  sx,
}) => {
  const { t, i18n } = useTranslation();
  const adapterLocale = getDateAdapterLocale(i18n.language);
  const startPopup = useStableDatePickerPopup({
    value: startValue,
    onChange: onStartChange,
  });
  const endPopup = useStableDatePickerPopup({
    value: endValue,
    onChange: onEndChange,
  });
  const groupLabel = label ?? t('common.dateRange.duration');
  const fromFieldLabel = fromLabel ?? t('common.dateRange.from');
  const toFieldLabel = toLabel ?? t('common.dateRange.to');

  return (
    <Box sx={sx}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: 600, color: error ? '#f44336' : BRAND.text }}
      >
        {groupLabel}
      </Typography>

      <LocalizationProvider
        adapterLocale={adapterLocale}
        dateAdapter={AdapterDateFns}
        localeText={getDatePickerLocaleText(t)}
      >
        <Box sx={dateRangeGroupSx()}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatePicker
              value={startPopup.value}
              onChange={startPopup.onChange}
              format={format}
              views={startPopup.views}
              open={startPopup.open}
              onOpen={startPopup.onOpen}
              onClose={startPopup.onClose}
              onViewChange={startPopup.onViewChange}
              disablePast={disablePast}
              disableFuture={disableFuture}
              maxDate={endValue ?? undefined}
              slotProps={buildInnerFieldSlotProps({
                label: fromFieldLabel,
                error,
                edge: 'start',
                onBlur: onStartBlur,
              })}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatePicker
              value={endPopup.value}
              onChange={endPopup.onChange}
              format={format}
              views={endPopup.views}
              open={endPopup.open}
              onOpen={endPopup.onOpen}
              onClose={endPopup.onClose}
              onViewChange={endPopup.onViewChange}
              disableFuture={disableFuture}
              minDate={startValue ?? undefined}
              slotProps={buildInnerFieldSlotProps({
                label: toFieldLabel,
                error,
                edge: 'end',
                onBlur: onEndBlur,
              })}
            />
          </Box>
        </Box>
      </LocalizationProvider>

      <Box sx={{ minHeight: 22, mt: 0.75, mx: 0.5 }}>
        {helperContent || (helperText ? (
          <FormHelperText sx={{ m: 0, color: error ? '#f44336' : 'text.secondary' }}>
            {helperText}
          </FormHelperText>
        ) : endOptional ? (
          <FormHelperText sx={{ m: 0, color: 'text.secondary' }}>
            {t('common.dateRange.endOptional')}
          </FormHelperText>
        ) : (
          <FormHelperText sx={{ m: 0, visibility: 'hidden', userSelect: 'none' }} aria-hidden>
            {'\u00A0'}
          </FormHelperText>
        ))}
      </Box>
    </Box>
  );
};

export default SalovareDateRangePicker;
